/**
 * matchOption — smart resolution of a spoken transcript to a dropdown option.
 *
 * Speech recognition rarely returns the exact option label ("O positive" vs
 * "O+", "boy" vs "Male"). This ranks the available options against a
 * normalized transcript so guided mode can pre-select the most likely value,
 * while still letting the user correct it in the preview dropdown before it
 * is committed. Returns null when nothing is confident enough — the caller
 * then shows the full list rather than writing a wrong value silently.
 */

/** Common spoken → canonical-label synonyms, matched on normalized text. */
const SYNONYMS = {
  // gender
  male: ['male', 'man', 'boy', 'm', 'gents', 'he'],
  female: ['female', 'woman', 'girl', 'f', 'lady', 'she'],
  other: ['other', 'others', 'transgender', 'non binary', 'nonbinary'],
  // blood groups (spoken → canonical fragments)
  'a+': ['a positive', 'a plus', 'a pos'],
  'a-': ['a negative', 'a minus', 'a neg'],
  'b+': ['b positive', 'b plus', 'b pos'],
  'b-': ['b negative', 'b minus', 'b neg'],
  'ab+': ['ab positive', 'ab plus', 'ab pos'],
  'ab-': ['ab negative', 'ab minus', 'ab neg'],
  'o+': ['o positive', 'o plus', 'o pos'],
  'o-': ['o negative', 'o minus', 'o neg'],
  // marital status
  single: ['single', 'unmarried', 'bachelor'],
  married: ['married', 'wedded'],
  divorced: ['divorced', 'separated'],
  widowed: ['widowed', 'widow', 'widower'],
};

/** Lowercase, trim, strip filler and punctuation. */
export function normalize(text) {
  return String(text)
    .toLowerCase()
    .replace(/^(it'?s|it is|my|the|i am|i'm|this is)\s+/i, '')
    .replace(/\s+(please|thanks|thank you)\s*$/i, '')
    .replace(/[.,!?;:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function labelOf(opt, getLabel) {
  return normalize(getLabel ? getLabel(opt) : opt);
}

/**
 * @param {string}   transcript      raw spoken text
 * @param {Array}    options         option list
 * @param {object}   [cfg]
 * @param {function} [cfg.getLabel]  option -> display label (default: identity)
 * @param {function} [cfg.getValue]  option -> committed value (default: identity)
 * @returns {{ option, value, label } | null}
 */
export function matchOption(transcript, options, { getLabel, getValue } = {}) {
  const t = normalize(transcript);
  if (!t || !Array.isArray(options) || options.length === 0) return null;

  const value = (opt) => (getValue ? getValue(opt) : opt);
  const wrap = (opt) => ({ option: opt, value: value(opt), label: getLabel ? getLabel(opt) : opt });

  // Expand the transcript with any synonym canonical keys it triggers, so
  // "o positive" can match an option whose label is "O+".
  const synonymKeys = Object.entries(SYNONYMS)
    .filter(([, phrases]) => phrases.some((p) => t === p || t.includes(p)))
    .map(([canonical]) => canonical);

  // 1. exact label match
  let hit = options.find((o) => labelOf(o, getLabel) === t);
  if (hit) return wrap(hit);

  // 2. synonym canonical match (compare against option label, ignoring spaces)
  for (const key of synonymKeys) {
    hit = options.find((o) => labelOf(o, getLabel).replace(/\s+/g, '') === key.replace(/\s+/g, ''));
    if (hit) return wrap(hit);
  }

  // 3. startsWith (either direction)
  hit = options.find((o) => {
    const l = labelOf(o, getLabel);
    return l && (l.startsWith(t) || t.startsWith(l));
  });
  if (hit) return wrap(hit);

  // 4. includes (either direction), longest label first to prefer specificity
  const byLen = [...options].sort(
    (a, b) => labelOf(b, getLabel).length - labelOf(a, getLabel).length,
  );
  hit = byLen.find((o) => {
    const l = labelOf(o, getLabel);
    return l && (l.includes(t) || t.includes(l));
  });
  if (hit) return wrap(hit);

  return null;
}
